/**
 * 로그인/로그아웃 시 사용자별 데이터 정리
 * - Zustand persist 스토어, localStorage 캐시, IndexedDB 오프라인 캐시
 * - 디바이스 설정(테마, 단축키, 투어 완료 등)은 유지
 */
import { useStore } from '../store/store';
import { useEditorHistoryStore } from '../store/editorHistoryStore';
import { useCollectionStore } from '../store/collectionStore';
import { clearAllPendingChanges } from '../services/offlineCache';
import { resetStatsCache } from '../hooks/useMyStats';

/** 사용자별 localStorage 키 (고정) */
const USER_SPECIFIC_KEYS = [
  'access_token',
  'refresh_token',
  'auth-storage',
  'auth_return_url',
  'md-label-maker-storage',
  'md-label-maker.collections.v1',
  'md-recent-albums',
  'md-export-history',
  'md-categories',
  'md-color-history',
  'userSelectedTextColors',
  'md-label-maker-accordion-state',
  'albums_sortBy',
  'albums_sortOrder',
  'albums_viewMode',
];

/** 동적 패턴으로 매칭되는 키 prefix */
const USER_SPECIFIC_PREFIXES = [
  'album-cache-',
  'md-pref-',
];

/**
 * 사용자별 캐시/스토리지 정리 (로그인·로그아웃 공통)
 * - 에디터 in-memory 스토어(albumInfo/tracklist/printUnits)는 리셋하지 않음
 *   → EditorPage가 마운트된 상태에서 리셋하면 자동저장이 빈 데이터로 서버를 덮어쓰는 경쟁 조건 발생
 * - 에디터 리셋이 필요하면 clearAllUserDataWithEditor()를 사용
 */
export function clearAllUserData(): void {
  // 1. 고정 키 제거
  for (const key of USER_SPECIFIC_KEYS) {
    localStorage.removeItem(key);
  }

  // 2. 동적 키 제거 (album-cache-*, md-pref-*)
  const allKeys = Object.keys(localStorage);
  for (const key of allKeys) {
    if (USER_SPECIFIC_PREFIXES.some((p) => key.startsWith(p))) {
      localStorage.removeItem(key);
    }
  }

  // 3. 모듈 스코프 캐시 초기화
  resetStatsCache();

  // 4. Zustand in-memory 스토어 리셋 (에디터 제외)
  useCollectionStore.getState().reset();

  // 5. IndexedDB 오프라인 캐시 삭제
  clearAllPendingChanges().catch(() => {});
  try {
    indexedDB.deleteDatabase('md-label-maker-cache');
  } catch {
    // IndexedDB 미지원 환경 무시
  }
}

/**
 * 로그아웃 전용 — 에디터 in-memory 스토어까지 포함하여 전체 리셋
 * EditorPage가 unmount된 후(navigate('/') 등) 호출하거나,
 * 이미 로그아웃 확정 시에만 사용
 */
export function clearAllUserDataWithEditor(): void {
  clearAllUserData();
  useStore.getState().resetForNewAlbum();
  useStore.temporal.getState().clear();
  useEditorHistoryStore.getState().clearHistory();
}
